import React, { useState, useEffect } from "react";
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Form,
	FormGroup,
	Label,
	Input,
	Alert,
	Spinner,
	CustomInput,
	Row,
	Col
} from "reactstrap";
import {
	optimizeRecipeAPI,
	OptimizationObjective,
	OptimizeRecipeRequest,
	getAllFormulationsAPI,
	Formulation
} from "../../services/formulationService";
import "../../assets/scss/modals/_optimization-modal.scss";

interface OptimizationModalProps {
	isOpen: boolean;
	toggle: () => void;
	onOptimizationComplete?: (data: any) => void;
}

const OptimizationModal: React.FC<OptimizationModalProps> = ({
	isOpen,
	toggle,
	onOptimizationComplete
}) => {
	const [selectedRecipeId, setSelectedRecipeId] = useState<string>("");
	const [objective, setObjective] = useState<OptimizationObjective>("lower_sugar");
	const [maxCostIncrease, setMaxCostIncrease] = useState<number | undefined>(undefined);
	const [maintainTaste, setMaintainTaste] = useState<boolean>(true);
	const [allergenRestrictions, setAllergenRestrictions] = useState<string[]>([]);
	const [dietaryRequirements, setDietaryRequirements] = useState<string[]>([]);
	const [isOptimizing, setIsOptimizing] = useState(false);
	const [optimizationResult, setOptimizationResult] = useState<any>(null);
	const [error, setError] = useState<string | null>(null);
	const [showResult, setShowResult] = useState(false);
	const [isLoadingFormulations, setIsLoadingFormulations] = useState(false);
	const [formulations, setFormulations] = useState<Formulation[]>([]);

	const objectiveOptions: { value: OptimizationObjective; label: string }[] = [
		{ value: "lower_sugar", label: "Lower Sugar" },
		{ value: "improve_sustainability", label: "Improve Sustainability" },
		{ value: "reduce_cost", label: "Reduce Cost" },
		{ value: "boost_protein", label: "Boost Protein" },
		{ value: "clean_label", label: "Clean Label" }
	];

	const commonAllergens = [
		"Milk",
		"Eggs",
		"Fish",
		"Shellfish",
		"Tree Nuts",
		"Peanuts",
		"Wheat",
		"Soybeans"
	];

	const dietaryOptions = [
		"Vegan",
		"Vegetarian",
		"Gluten-Free",
		"Keto",
		"Paleo",
		"Organic",
		"Non-GMO",
		"Halal",
		"Kosher"
	];

	useEffect(() => {
		if (isOpen) {
			loadFormulations();
		}
	}, [isOpen]);

	const loadFormulations = async () => {
		setIsLoadingFormulations(true);
		setError(null);
		try {
			const result = await getAllFormulationsAPI();
			if (result.success && result.data) {
				setFormulations(result.data);
			} else {
				setError(result.error || "Failed to load formulations");
				setFormulations([]);
			}
		} catch (err) {
			console.error("Error loading formulations:", err);
			setError("Failed to load formulations. Please try again.");
			setFormulations([]);
		} finally {
			setIsLoadingFormulations(false);
		}
	};

	const handleAllergenChange = (allergen: string, checked: boolean) => {
		if (checked) {
			setAllergenRestrictions([...allergenRestrictions, allergen]);
		} else {
			setAllergenRestrictions(allergenRestrictions.filter((a) => a !== allergen));
		}
	};

	const handleDietaryChange = (diet: string, checked: boolean) => {
		if (checked) {
			setDietaryRequirements([...dietaryRequirements, diet]);
		} else {
			setDietaryRequirements(dietaryRequirements.filter((d) => d !== diet));
		}
	};

	const handleOptimize = async () => {
		if (!selectedRecipeId) {
			setError("Please select a recipe/product");
			return;
		}

		setIsOptimizing(true);
		setError(null);
		setOptimizationResult(null);
		setShowResult(false);

		try {
			const request: OptimizeRecipeRequest = {
				objective,
				max_cost_increase: maxCostIncrease,
				maintain_taste: maintainTaste,
				allergen_restrictions:
					allergenRestrictions.length > 0 ? allergenRestrictions : undefined,
				dietary_requirements:
					dietaryRequirements.length > 0 ? dietaryRequirements : undefined
			};

			const result = await optimizeRecipeAPI(selectedRecipeId, request);

			if (!result.success) {
				throw new Error(result.error || "Failed to optimize recipe");
			}

			setOptimizationResult(result.data);
			setShowResult(true);

			if (onOptimizationComplete) {
				onOptimizationComplete(result.data);
			}
		} catch (err) {
			console.error("Error optimizing recipe:", err);
			const errorMessage =
				err instanceof Error
					? err.message
					: "Failed to optimize recipe. Please try again.";
			setError(errorMessage);
			setOptimizationResult(null);
			setShowResult(false);
		} finally {
			setIsOptimizing(false);
		}
	};

	const handleClose = () => {
		setSelectedRecipeId("");
		setObjective("lower_sugar");
		setMaxCostIncrease(undefined);
		setMaintainTaste(true);
		setAllergenRestrictions([]);
		setDietaryRequirements([]);
		setOptimizationResult(null);
		setError(null);
		setShowResult(false);
		toggle();
	};

	const handleNewOptimization = () => {
		setOptimizationResult(null);
		setShowResult(false);
		setError(null);
		setSelectedRecipeId("");
	};

	const selectedFormulation = formulations.find((f) => f.id === selectedRecipeId);

	const formatLabel = (key: string): string => {
		return key
			.split("_")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ");
	};

	const formatValue = (value: unknown, isCurrency: boolean = false): string => {
		if (typeof value === "number") {
			return isCurrency ? `$${value.toFixed(2)}` : value.toFixed(1);
		}
		if (typeof value === "string") {
			return value;
		}
		return "0.0";
	};

	const renderOptimizationResult = (data: any) => {
		if (!data) return null;

		// Extract the optimized formulation data
		const optimizedFormulation = data.optimized_formulation || {};
		const ingredients = optimizedFormulation.ingredients || [];
		const calculatedScores = optimizedFormulation.calculated_scores || {};
		const scoresBreakdown = calculatedScores.breakdown || {};
		const improvements = data.improvements || {};
		const changes = data.changes || [];
		const rationale = data.rationale || optimizedFormulation.rationale || "";

		return (
			<div className="optimization-result">
				{/* Optimized Formulation Header */}
				{optimizedFormulation.name && (
					<div className="optimization-header mb-4">
						<h5 className="mb-2">{optimizedFormulation.name}</h5>
						{optimizedFormulation.description && (
							<p className="text-muted mb-0">{optimizedFormulation.description}</p>
						)}
					</div>
				)}

				{/* Improvements Summary */}
				{(improvements.nutrition_improvement ||
					improvements.cost_reduction !== undefined ||
					improvements.sustainability_improvement !== undefined) && (
						<div className="improvements-section mb-4 p-3 bg-light rounded">
							<h6 className="mb-3">Improvements</h6>
							<Row>
								{improvements.nutrition_improvement !== undefined && (
									<Col md={4} className="mb-2">
										<div className="improvement-item">
											<strong>Nutrition:</strong>{" "}
											<span className={improvements.nutrition_improvement > 0 ? "text-success" : ""}>
												{improvements.nutrition_improvement > 0 ? "+" : ""}
												{improvements.nutrition_improvement.toFixed(1)}%
											</span>
										</div>
									</Col>
								)}
								{improvements.cost_reduction !== undefined && (
									<Col md={4} className="mb-2">
										<div className="improvement-item">
											<strong>Cost:</strong>{" "}
											<span className={improvements.cost_reduction > 0 ? "text-success" : ""}>
												{improvements.cost_reduction > 0 ? "-" : "+"}
												{Math.abs(improvements.cost_reduction).toFixed(1)}%
											</span>
										</div>
									</Col>
								)}
								{improvements.sustainability_improvement !== undefined && (
									<Col md={4} className="mb-2">
										<div className="improvement-item">
											<strong>Sustainability:</strong>{" "}
											<span
												className={
													improvements.sustainability_improvement > 0 ? "text-success" : ""
												}
											>
												{improvements.sustainability_improvement > 0 ? "+" : ""}
												{improvements.sustainability_improvement.toFixed(1)}%
											</span>
										</div>
									</Col>
								)}
							</Row>
						</div>
					)}

				{/* Calculated Scores */}
				{calculatedScores.overall_score !== undefined && (
					<div className="scores-section mb-4">
						<h6 className="mb-3">Performance Scores</h6>
						<Row>
							{calculatedScores.nutrition_score !== undefined && (
								<Col md={6} className="mb-3">
									<div className="score-item">
										<div className="score-label">Nutrition Score</div>
										<div className="score-value">
											{typeof calculatedScores.nutrition_score === "number"
												? calculatedScores.nutrition_score.toFixed(1)
												: calculatedScores.nutrition_score || "0.0"}
										</div>
									</div>
								</Col>
							)}
							{calculatedScores.roi_score !== undefined && (
								<Col md={6} className="mb-3">
									<div className="score-item">
										<div className="score-label">ROI Score</div>
										<div className="score-value">
											{typeof calculatedScores.roi_score === "number"
												? calculatedScores.roi_score.toFixed(1)
												: calculatedScores.roi_score || "0.0"}
										</div>
									</div>
								</Col>
							)}
							{calculatedScores.sustainability_score !== undefined && (
								<Col md={6} className="mb-3">
									<div className="score-item">
										<div className="score-label">Sustainability Score</div>
										<div className="score-value">
											{typeof calculatedScores.sustainability_score === "number"
												? calculatedScores.sustainability_score.toFixed(1)
												: calculatedScores.sustainability_score || "0.0"}
										</div>
									</div>
								</Col>
							)}
							{calculatedScores.overall_score !== undefined && (
								<Col md={6} className="mb-3">
									<div className="score-item overall-score">
										<div className="score-label">Overall Score</div>
										<div className="score-value">
											{typeof calculatedScores.overall_score === "number"
												? calculatedScores.overall_score.toFixed(1)
												: calculatedScores.overall_score || "0.0"}
										</div>
									</div>
								</Col>
							)}
						</Row>
					</div>
				)}

				{/* Score Breakdown */}
				{(scoresBreakdown.nutrition ||
					scoresBreakdown.roi ||
					scoresBreakdown.sustainability) && (
						<div className="score-breakdown-section mb-4">
							{scoresBreakdown.nutrition && Object.keys(scoresBreakdown.nutrition).length > 0 && (
								<div className="mb-3">
									<h6 className="mb-2">Nutrition Breakdown</h6>
									<Row>
										{Object.entries(scoresBreakdown.nutrition).map(([key, value]) => (
											<Col key={key} md={6} className="mb-2">
												<div className="score-item">
													<div className="score-label">{formatLabel(key)}</div>
													<div className="score-value">{formatValue(value)}</div>
												</div>
											</Col>
										))}
									</Row>
								</div>
							)}
							{scoresBreakdown.roi && Object.keys(scoresBreakdown.roi).length > 0 && (
								<div className="mb-3">
									<h6 className="mb-2">ROI Breakdown</h6>
									<Row>
										{Object.entries(scoresBreakdown.roi).map(([key, value]) => (
											<Col key={key} md={6} className="mb-2">
												<div className="score-item">
													<div className="score-label">{formatLabel(key)}</div>
													<div className="score-value">
														{formatValue(
															value,
															key === "cost_per_serving" || key === "market_price"
														)}
													</div>
												</div>
											</Col>
										))}
									</Row>
								</div>
							)}
							{scoresBreakdown.sustainability &&
								Object.keys(scoresBreakdown.sustainability).length > 0 && (
									<div className="mb-3">
										<h6 className="mb-2">Sustainability Breakdown</h6>
										<Row>
											{Object.entries(scoresBreakdown.sustainability).map(([key, value]) => (
												<Col key={key} md={6} className="mb-2">
													<div className="score-item">
														<div className="score-label">{formatLabel(key)}</div>
														<div className="score-value">{formatValue(value)}</div>
													</div>
												</Col>
											))}
										</Row>
									</div>
								)}
						</div>
					)}

				{/* Ingredients List */}
				{ingredients.length > 0 && (
					<div className="ingredients-section mb-4">
						<h6 className="mb-3">Optimized Ingredients</h6>
						<div className="table-responsive">
							<table className="table table-sm table-bordered">
								<thead>
									<tr>
										<th>Ingredient</th>
										<th>Percentage</th>
										<th>Weight</th>
										<th>Cost</th>
									</tr>
								</thead>
								<tbody>
									{ingredients.map((ingredient: any, index: number) => (
										<tr key={ingredient.ingredient_id || index}>
											<td>
												<strong>{ingredient.name}</strong>
												{ingredient.rationale && (
													<div className="text-muted small mt-1">
														{ingredient.rationale}
													</div>
												)}
											</td>
											<td>{ingredient.percentage?.toFixed(1)}%</td>
											<td>
												{ingredient.weight?.toFixed(2)} {ingredient.unit || "g"}
											</td>
											<td>${ingredient.cost?.toFixed(2)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				)}

				{/* Changes Made */}
				{changes.length > 0 && (
					<div className="changes-section mb-4">
						<h6 className="mb-3">Changes Made</h6>
						<div className="list-group">
							{changes.map((change: any, index: number) => (
								<div key={index} className="list-group-item">
									<div className="d-flex w-100 justify-content-between">
										<h6 className="mb-1">
											{change.type
												?.split("_")
												.map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
												.join(" ")}
										</h6>
									</div>
									{change.original_ingredient && (
										<p className="mb-1">
											<strong>From:</strong> {change.original_ingredient}
										</p>
									)}
									{change.new_ingredient && (
										<p className="mb-1">
											<strong>To:</strong> {change.new_ingredient}
										</p>
									)}
									{change.reason && (
										<p className="mb-0 text-muted small">
											<em>{change.reason}</em>
										</p>
									)}
								</div>
							))}
						</div>
					</div>
				)}

				{/* Rationale */}
				{rationale && (
					<div className="rationale-section mb-3 p-3 bg-light rounded">
						<h6 className="mb-2">Optimization Rationale</h6>
						<p className="mb-0">{rationale}</p>
					</div>
				)}

				{/* Calculated At Timestamp */}
				{calculatedScores.calculated_at && (
					<div className="optimization-timestamp mt-3 pt-3 border-top">
						<small className="text-muted">
							Calculated at: {new Date(calculatedScores.calculated_at).toLocaleString()}
						</small>
					</div>
				)}
			</div>
		);
	};

	return (
		<Modal
			isOpen={isOpen}
			toggle={handleClose}
			size="xl"
			className="optimization-modal"
		>
			<ModalHeader toggle={handleClose}>Optimize Recipe</ModalHeader>
			<ModalBody>
				{!showResult ? (
					<div className="optimization-form-container">
						<Form className="optimization-form">
							<div className="form-section">
								<FormGroup>
									<Label for="recipeSelect" className="form-label">
										Select Recipe/Product <span className="required-asterisk">*</span>
									</Label>
									{isLoadingFormulations ? (
										<div className="text-center py-3">
											<Spinner size="sm" color="primary" />
											<p className="mt-2">Loading formulations...</p>
										</div>
									) : formulations.length === 0 ? (
										<Alert color="warning">
											No formulations available. Please create a formulation first.
										</Alert>
									) : (
										<Input
											type="select"
											id="recipeSelect"
											value={selectedRecipeId}
											onChange={(e) => setSelectedRecipeId(e.target.value)}
											disabled={isOptimizing}
											className="form-control-custom"
										>
											<option value="">-- Select A Recipe/Product --</option>
											{formulations.map((formulation) => (
												<option key={formulation.id} value={formulation.id}>
													{formulation.name}
												</option>
											))}
										</Input>
									)}
									{selectedFormulation && (
										<div className="selected-product-info mt-2">
											<small>
												<strong>Formulation:</strong> {selectedFormulation.name}
											</small>
										</div>
									)}
								</FormGroup>
							</div>

							<div className="form-section-divider"></div>

							<div className="form-section">
								<FormGroup>
									<Label for="objective" className="form-label">
										Optimization Objective <span className="required-asterisk">*</span>
									</Label>
									<Input
										type="select"
										id="objective"
										value={objective}
										onChange={(e) =>
											setObjective(e.target.value as OptimizationObjective)
										}
										disabled={isOptimizing}
										className="form-control-custom"
									>
										{objectiveOptions.map((option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</Input>
								</FormGroup>
							</div>

							<div className="form-section-divider"></div>

							<div className="form-section">
								<Row>
									<Col md={7}>
										<FormGroup>
											<Label for="maxCostIncrease" className="form-label">
												Max Cost Increase (%)
											</Label>
											<Input
												type="number"
												id="maxCostIncrease"
												value={maxCostIncrease || ""}
												onChange={(e) =>
													setMaxCostIncrease(
														e.target.value ? parseFloat(e.target.value) : undefined
													)
												}
												placeholder="e.g., 10"
												min="0"
												step="0.1"
												disabled={isOptimizing}
												className="form-control-custom"
											/>
											<small className="form-text text-muted">
												Maximum percentage increase in cost allowed
											</small>
										</FormGroup>
									</Col>
									<Col md={5}>
										<FormGroup className="toggle-group">
											<Label className="form-label toggle-label">Maintain Taste</Label>
											<div className="toggle-wrapper">
												<CustomInput
													type="switch"
													id="maintainTaste"
													label="Maintain Taste"
													checked={maintainTaste}
													onChange={(e) => setMaintainTaste(e.target.checked)}
													disabled={isOptimizing}
													className="custom-switch"
												/>
											</div>
										</FormGroup>
									</Col>
								</Row>
							</div>

							<div className="form-section-divider"></div>

							<div className="form-section">
								<h6 className="section-title">
									Restrictions <span className="optional-badge">(Optional)</span>
								</h6>
								<FormGroup>
									<Label className="form-label section-label">Allergen Restrictions</Label>
									<div className="checkbox-group">
										{commonAllergens.map((allergen) => (
											<CustomInput
												key={allergen}
												type="checkbox"
												id={`allergen-opt-${allergen}`}
												label={allergen}
												checked={allergenRestrictions.includes(allergen)}
												onChange={(e) =>
													handleAllergenChange(allergen, e.target.checked)
												}
												disabled={isOptimizing}
												className="checkbox-item"
											/>
										))}
									</div>
								</FormGroup>
							</div>

							<div className="form-section-divider"></div>

							<div className="form-section">
								<FormGroup>
									<Label className="form-label section-label">Dietary Requirements</Label>
									<div className="checkbox-group">
										{dietaryOptions.map((diet) => (
											<CustomInput
												key={diet}
												type="checkbox"
												id={`dietary-opt-${diet}`}
												label={diet}
												checked={dietaryRequirements.includes(diet)}
												onChange={(e) => handleDietaryChange(diet, e.target.checked)}
												disabled={isOptimizing}
												className="checkbox-item"
											/>
										))}
									</div>
								</FormGroup>
							</div>
						</Form>

						{error && (
							<Alert color="danger" className="mt-3">
								{error}
							</Alert>
						)}

						{isOptimizing && (
							<div className="text-center mt-3">
								<Spinner color="primary" />
								<p className="mt-2">Optimizing recipe with AI...</p>
							</div>
						)}
					</div>
				) : (
					<div>
						<Alert color="success" className="mb-3">
							<strong>Recipe Optimized Successfully!</strong>
						</Alert>
						{selectedFormulation && (
							<div className="selected-product-info mb-3 p-2 bg-light rounded">
								<small>
									<strong>Formulation:</strong> {selectedFormulation.name}
									<br />
									<strong>Objective:</strong>{" "}
									{objectiveOptions.find((o) => o.value === objective)?.label}
								</small>
							</div>
						)}
						{optimizationResult && renderOptimizationResult(optimizationResult)}
					</div>
				)}
			</ModalBody>
			<ModalFooter>
				{!showResult ? (
					<>
						<Button color="secondary" onClick={handleClose} disabled={isOptimizing}>
							Cancel
						</Button>
						<Button
							color="primary"
							onClick={handleOptimize}
							disabled={isOptimizing || !selectedRecipeId || isLoadingFormulations}
						>
							{isOptimizing ? (
								<>
									<Spinner size="sm" className="mr-2" />
									Optimizing...
								</>
							) : (
								"Optimize Recipe"
							)}
						</Button>
					</>
				) : (
					<>
						<Button color="secondary" onClick={handleNewOptimization}>
							Optimize Another
						</Button>
						<Button color="success" onClick={handleClose}>
							Close
						</Button>
					</>
				)}
			</ModalFooter>
		</Modal>
	);
};

export default OptimizationModal;

